import React, { useState } from 'react';
import {
  ASSUMED_UPLOAD_BPS,
  MAX_VIDEOS_PER_PROJECT,
  formatBytes,
  formatDuration,
} from '../../videoUpload/videoCaps';

// Preflight for a multi-video footage batch — the peer of PhotoUploadDialog.
//
// The one thing this dialog exists to get right is ORDER. A later phase
// concatenates these clips into a single video, so the sequence shown here is
// the sequence the finished timelapse plays in, and a wrong default silently
// produces a scrambled result. The list is therefore the body of the dialog,
// not a footnote: numbered, reorderable by drag AND by keyboard-reachable
// up/down buttons, with the rule that produced the default stated out loud.
//
// Durations read "—" on purpose. Browsers refuse to load metadata for codecs
// they can't decode — the Brinno MJPEG-in-MP4 case this feature is for — so a
// client-side duration would appear for some files and not others. The server
// probe fills them in after upload; showing "—" for all of them until then is
// the honest version.

const DurationCell = () => (
  <span className="pi-video-row-dur" title="Read from the file after upload">—</span>
);

const VideoUploadDialog = ({
  phase,
  scan,
  dedup,
  ordered,
  caps,
  poolCaps,
  onMove,
  onCancel,
  onConfirm,
}) => {
  const [dragIndex, setDragIndex] = useState(null);

  if (phase !== 'preflight' || !scan || !dedup || !caps) return null;

  const items = ordered || [];
  const toUploadCount = items.length;
  const toUploadBytes = dedup.totals.toUploadBytes;
  const alreadyCount = dedup.totals.alreadyUploadedCount;
  const skippedCount = scan.skippedNonVideos.length;
  // Clips dropped because another file in this same batch was byte-identical.
  // Surfaced rather than swallowed: the batch is smaller than what the user
  // picked, and they are the only one who can tell us that was unintended.
  const duplicateCount = scan.duplicateCount || 0;
  const isResume = alreadyCount > 0;
  const etaSeconds = ASSUMED_UPLOAD_BPS > 0 ? toUploadBytes / ASSUMED_UPLOAD_BPS : null;
  const blocked = !!(caps.blocked || poolCaps?.blocked);
  const nothingToUpload = toUploadCount === 0 && !blocked;

  const handleDrop = (targetIndex) => {
    if (dragIndex == null || !onMove) return;
    onMove(dragIndex, targetIndex);
    setDragIndex(null);
  };

  return (
    <div className="pi-overlay" role="dialog" aria-modal="true">
      <div className="pi-modal">
        <h2 className="pi-modal-title">
          {isResume
            ? `Resume upload — ${toUploadCount.toLocaleString()} clips left`
            : `${toUploadCount.toLocaleString()} video${toUploadCount === 1 ? '' : 's'} ready to upload`}
        </h2>
        <div className="pi-modal-sub">
          {formatBytes(toUploadBytes)} total.{' '}
          {scan.orderingBasis === 'capturedAt'
            ? 'Ordered by capture time — drag to change the playback order.'
            : 'Ordered by file name (the files share no usable capture time) — drag to change the playback order.'}
        </div>

        {toUploadCount > 0 && (
          <div className="pi-modal-section">
            <h4 className="pi-section-head">Playback order</h4>
            <ol className="pi-video-list" data-testid="video-order-list">
              {items.map((entry, index) => (
                <li
                  key={entry.videoKey}
                  className={`pi-video-row${dragIndex === index ? ' pi-video-row--dragging' : ''}`}
                  data-testid={`video-row-${index}`}
                  draggable
                  onDragStart={() => setDragIndex(index)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleDrop(index)}
                  onDragEnd={() => setDragIndex(null)}
                >
                  <span className="pi-video-row-idx">{index + 1}</span>
                  <span className="pi-video-row-name" title={entry.fileName}>{entry.fileName}</span>
                  <span className="pi-video-row-size">{formatBytes(entry.sizeBytes)}</span>
                  <DurationCell />
                  {/* Buttons, not drag alone: drag-and-drop is unreachable by
                      keyboard and unusable on touch, and reordering is the one
                      thing this dialog must not make optional. */}
                  <span className="pi-video-row-actions">
                    <button
                      type="button"
                      className="pi-video-move"
                      aria-label={`Move ${entry.fileName} earlier`}
                      disabled={index === 0}
                      onClick={() => onMove && onMove(index, index - 1)}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="pi-video-move"
                      aria-label={`Move ${entry.fileName} later`}
                      disabled={index === items.length - 1}
                      onClick={() => onMove && onMove(index, index + 1)}
                    >
                      ↓
                    </button>
                  </span>
                </li>
              ))}
            </ol>
          </div>
        )}

        <div className="pi-modal-section">
          {isResume && (
            <div className="pi-stat-row">
              <span className="pi-k">Already in this project (skipped)</span>
              <span className="pi-v">{alreadyCount.toLocaleString()} video{alreadyCount === 1 ? '' : 's'}</span>
            </div>
          )}
          {duplicateCount > 0 && (
            <div className="pi-stat-row" data-testid="video-duplicate-row">
              <span className="pi-k">Duplicates in this selection (skipped)</span>
              <span className="pi-v">{duplicateCount.toLocaleString()} file{duplicateCount === 1 ? '' : 's'}</span>
            </div>
          )}
          <div className="pi-stat-row">
            <span className="pi-k">{isResume ? 'Left to upload' : 'Videos to upload'}</span>
            <span className="pi-v">
              {toUploadCount.toLocaleString()} video{toUploadCount === 1 ? '' : 's'} · {formatBytes(toUploadBytes)}
            </span>
          </div>
          {etaSeconds != null && toUploadCount > 0 && (
            <div className="pi-stat-row">
              <span className="pi-k">Estimated upload time (assumes 20 Mbps up)</span>
              <span className="pi-v">{formatDuration(etaSeconds)}</span>
            </div>
          )}
          {poolCaps?.hasPool && (
            <div className={`pi-stat-row${poolCaps.overPoolCap ? ' pi-stat-row--warn' : ''}`}>
              <span className="pi-k">Account storage after upload</span>
              <span className="pi-v">
                {poolCaps.poolPending
                  ? `calculating… of ${formatBytes(poolCaps.poolTotalBytes)}`
                  : `${formatBytes(poolCaps.projectedPoolBytes)} of ${formatBytes(poolCaps.poolTotalBytes)}`}
              </span>
            </div>
          )}
          <div className="pi-stat-row">
            <span className="pi-k">Videos in this project after upload</span>
            <span className="pi-v">{caps.projectedVideos.toLocaleString()}</span>
          </div>
        </div>

        {skippedCount > 0 && (
          <div className="pi-modal-section">
            <div className="pi-warn-line">
              {skippedCount} file{skippedCount === 1 ? '' : 's'} skipped — not videos.
            </div>
          </div>
        )}

        {blocked && (
          <div className="pi-modal-section pi-modal-error" data-testid="video-preflight-error">
            {caps.overFileCount && (
              <p className="pi-error-text">
                A project holds up to {MAX_VIDEOS_PER_PROJECT} videos and this would make{' '}
                {caps.projectedVideos.toLocaleString()}.{' '}
                {caps.slotsRemaining > 0
                  ? `Remove ${(caps.projectedVideos - MAX_VIDEOS_PER_PROJECT).toLocaleString()} clip(s) from this batch, or start a second project.`
                  : 'Start a second project for the rest of the shoot.'}
              </p>
            )}
            {caps.hasOversize && (
              <p className="pi-error-text">
                {caps.oversizeEntries.length} file{caps.oversizeEntries.length === 1 ? ' is' : 's are'} larger
                than {formatBytes(caps.maxVideoBytes)} and can&apos;t be uploaded:{' '}
                {caps.oversizeEntries.map((e) => e.fileName).join(', ')}.
              </p>
            )}
            {poolCaps?.poolPending && (
              <p className="pi-error-text">
                Checking your account storage… we&apos;ll enable the upload as soon as we know how much
                pooled space you have left.
              </p>
            )}
            {poolCaps?.overPoolCap && (
              <p className="pi-error-text">
                This upload would exceed your plan&apos;s {formatBytes(poolCaps.poolTotalBytes)} of pooled
                account storage ({formatBytes(poolCaps.projectedPoolBytes)} projected across all your
                projects). Free up space or upgrade your plan to add more.
              </p>
            )}
          </div>
        )}

        {nothingToUpload && (
          <div className="pi-modal-section pi-modal-note">
            Nothing new to upload — every clip you picked is already in this project.
          </div>
        )}

        <div className="pi-modal-foot">
          <div className="pi-note">
            Keep this tab open while uploading. If it stops, pick the same clips again — we&apos;ll skip
            whatever already made it.
          </div>
          <button type="button" className="btn ghost pi-btn" onClick={onCancel}>Cancel</button>
          <button
            type="button"
            className="btn btn-teal pi-btn"
            onClick={onConfirm}
            disabled={blocked || nothingToUpload}
          >
            {isResume ? 'Resume upload' : 'Start upload'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoUploadDialog;
